<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3" class="page-header-card">
            <v-card-title class="pb-2 pt-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-btn
                      color="grey darken-1"
                      outlined
                      small
                      @click="$router.back()"
                      class="mr-3"
                    >
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                    <div>
                      <h2 class="text-h6 font-weight-bold primary--text mb-0">
                        Order Items Details
                      </h2>
                      <p class="text-caption grey--text ma-0 mt-1">
                        View and manage items for order 
                        <v-chip v-if="orderInfo" x-small color="primary" outlined class="ml-1">
                          {{ orderInfo.order_number }}
                        </v-chip>
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center"></div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <!-- Items Table -->
        <v-col cols="12" :md="orderItems.length > 0 && !isSeller ? 8 : 12">
          <v-card elevation="2" style="overflow: hidden;">
            <v-card-title class="py-2 px-4 d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="primary" size="20" class="mr-2">mdi-package-variant-closed</v-icon>
                <span class="text-body-1 font-weight-semibold">Order Items</span>
                <v-chip x-small color="primary" outlined class="ml-2">
                  {{ orderItems.length }} {{ orderItems.length === 1 ? 'item' : 'items' }}
                </v-chip>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Loading -->
            <div v-if="isLoading" class="d-flex align-center justify-center py-12">
              <health-loader />
            </div>

            <!-- Table -->
            <v-data-table
              v-else
              :headers="headers"
              :items="orderItems"
              hide-default-footer
              class="items-table"
              :items-per-page="-1"
            >
              <template #[`item.sr_no`]="{ index }">
                <span class="font-weight-medium grey--text">{{ index + 1 }}</span>
              </template>

              <template #[`item.product_info`]="{ item }">
                <div>
                  <div
                    class="text-body-2 font-weight-medium"
                    :class="item.is_cancelled ? 'text-decoration-line-through grey--text' : ''"
                  >
                    {{ item.seller_product_variant && item.seller_product_variant.product_variant
                       ? item.seller_product_variant.product_variant.name
                       : (item.product ? item.product.name : 'Unknown') }}
                  </div>
                  <div v-if="item.is_cancelled && item.cancellation_reason" class="text-caption error--text mt-1">
                    Reason: {{ item.cancellation_reason }}
                  </div>
                </div>
              </template>

              <template #[`item.quantity`]="{ item }">
                <span class="font-weight-medium">{{ item.quantity }}</span>
              </template>

              <template #[`item.product_price`]="{ item }">
                <span>₹{{ getMrp(item).toFixed(2) }}</span>
              </template>

              <template #[`item.discount`]="{ item }">
                <span class="error--text">−₹{{ getItemDiscount(item).toFixed(2) }}</span>
              </template>

              <template #[`item.taxable`]="{ item }">
                <span>₹{{ parseFloat(item.amount || 0).toFixed(2) }}</span>
              </template>

              <template #[`item.hsn`]="{ item }">
                <span class="text-caption grey--text">{{ item.product && item.product.hsn_code ? item.product.hsn_code : '—' }}</span>
              </template>

              <template #[`item.gst_pct`]="{ item }">
                <span class="text-caption">{{ item.tax_percentage || 0 }}%</span>
              </template>

              <template #[`item.gst_amt`]="{ item }">
                <span>₹{{ parseFloat(item.tax_amount || 0).toFixed(2) }}</span>
              </template>

              <template #[`item.total_price`]="{ item }">
                <span
                  class="font-weight-bold"
                  :class="item.is_cancelled ? 'grey--text text-decoration-line-through' : 'success--text'"
                >₹{{ parseFloat(item.final_amount || 0).toFixed(2) }}</span>
              </template>

              <template #[`item.cancel_action`]="{ item }">
                <div class="d-flex flex-column align-center justify-center" style="gap:4px">
                  <template v-if="item.is_cancelled">
                    <v-chip x-small color="error" outlined>
                      <v-icon left x-small>mdi-close-circle</v-icon>
                      Cancelled
                    </v-chip>
                    <span
                      v-if="item.refund_amount && parseFloat(item.refund_amount) > 0"
                      class="text-caption font-weight-medium success--text"
                    >
                      <v-icon x-small color="success">mdi-cash-refund</v-icon>
                      ₹{{ parseFloat(item.refund_amount).toFixed(2) }} refunded
                    </span>
                    <span
                      v-else-if="item.refund_status === 'not_applicable'"
                      class="text-caption grey--text"
                    >No refund (COD)</span>
                  </template>
                  <v-btn
                    v-else-if="canCancelItems"
                    x-small color="error" outlined
                    @click="promptCancelItem(item)"
                  >
                    <v-icon left x-small>mdi-cancel</v-icon>
                    {{ isLastItem ? 'Cancel Order' : 'Cancel' }}
                  </v-btn>
                  <span v-else class="grey--text text-caption">—</span>
                </div>
              </template>

              <template #no-data>
                <div class="text-center py-10">
                  <v-icon size="48" color="grey lighten-3">mdi-package-variant-closed</v-icon>
                  <p class="grey--text mt-2 mb-0">No items found.</p>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>

        <!-- Order Summary Card -->
        <v-col cols="12" md="4" v-if="orderItems.length > 0 && !isSeller">
          <v-card elevation="2" class="summary-card">
            <v-card-title class="summary-header py-3 px-4">
              <v-icon color="white" size="18" class="mr-2">mdi-receipt-text-outline</v-icon>
              <span class="white--text text-body-2 font-weight-semibold">Order Summary</span>
            </v-card-title>

            <v-card-text class="pa-0">
              <div class="summary-row">
                <span class="summary-label">Total Qty</span>
                <span class="summary-value font-weight-medium">{{ getTotalQty() }}</span>
              </div>
              <v-divider></v-divider>
              <div class="summary-row">
                <span class="summary-label">MRP Total</span>
                <span class="summary-value">₹{{ calculateGrandTotal() }}</span>
              </div>
              <div class="summary-row" v-if="getTotalDiscount() > 0">
                <span class="summary-label">Discount</span>
                <span class="summary-value error--text">− ₹{{ getTotalDiscount().toFixed(2) }}</span>
              </div>
              <v-divider></v-divider>
              <div class="summary-row">
                <span class="summary-label font-weight-medium">Subtotal (incl. GST)</span>
                <span class="summary-value font-weight-bold">₹{{ getSubtotal().toFixed(2) }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label grey--text">GST Included ({{ getGstPct() }}%)</span>
                <span class="summary-value grey--text">(₹{{ getTotalGst().toFixed(2) }})</span>
              </div>
              <div class="summary-row" v-if="orderInfo && parseFloat(orderInfo.delivery_charges) > 0">
                <span class="summary-label">Shipping</span>
                <span class="summary-value">₹{{ parseFloat(orderInfo.delivery_charges).toFixed(2) }}</span>
              </div>
              <v-divider></v-divider>
              <div class="summary-row summary-row--payable">
                <span class="summary-label font-weight-bold">Payable Amount</span>
                <span class="summary-value summary-value--payable">₹{{ getPayableAmount().toFixed(2) }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Order Details Dialog -->
    <v-dialog
      v-model="showOrderDetailsDialog"
      max-width="800px"
      persistent
    >
      <v-card class="order-details-dialog">
        <v-card-title class="order-details-header py-3 px-4">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="8" class="pa-0">
              <div class="d-flex align-center">
                <v-icon color="white" class="mr-3">mdi-receipt-text-outline</v-icon>
                <div>
                  <h3 class="white--text font-weight-bold mb-0">Complete Order Details</h3>
                  <p class="white--text text-caption ma-0 mt-1 opacity-80">
                    Order #{{ orderInfo?.order_number }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="pa-0">
              <div class="d-flex justify-end">
                <v-btn
                  icon
                  @click="showOrderDetailsDialog = false"
                  color="white"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text class="pa-0" v-if="orderInfo">
          <!-- Order Information Tabs -->
          <v-tabs class="order-details-tabs" color="primary">
            <v-tab class="font-weight-medium">
              <v-icon left>mdi-information</v-icon>
              Basic Info
            </v-tab>
            <v-tab class="font-weight-medium">
              <v-icon left>mdi-account-group</v-icon>
              Parties
            </v-tab>
            <v-tab class="font-weight-medium">
              <v-icon left>mdi-calendar-clock</v-icon>
              Timeline
            </v-tab>
            
            <v-tab-item>
              <v-card-text class="py-4">
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="detail-item mb-4">
                      <div class="detail-label">Order Number</div>
                      <div class="detail-value">{{ orderInfo.order_number }}</div>
                    </div>
                    <div class="detail-item mb-4">
                      <div class="detail-label">Final Amount</div>
                      <div class="detail-value success--text font-weight-bold">
                        ₹{{ parseFloat(orderInfo.final_amount || 0).toFixed(2) }}
                      </div>
                    </div>
                    <div class="detail-item mb-4">
                      <div class="detail-label">Order Status</div>
                      <status-chip :color="getOrderStatusColor(orderInfo)" :icon="getOrderStatusIcon(orderInfo)" :label="String(getOrderStatusText(orderInfo))" />
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="detail-item mb-4">
                      <div class="detail-label">Order Date</div>
                      <div class="detail-value">{{ formatDate(orderInfo.created_at) }}</div>
                    </div>
                    <div class="detail-item mb-4" v-if="orderInfo.delivered_at">
                      <div class="detail-label">Delivered Date</div>
                      <div class="detail-value">{{ formatDate(orderInfo.delivered_at) }}</div>
                    </div>
                    <div class="detail-item mb-4" v-if="orderInfo.cancelled_at">
                      <div class="detail-label">Cancelled Date</div>
                      <div class="detail-value error--text">{{ formatDate(orderInfo.cancelled_at) }}</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-tab-item>
            
            <v-tab-item>
              <v-card-text class="py-4">
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="party-card">
                      <div class="party-header">
                        <v-icon color="primary" class="mr-2">mdi-account-tie</v-icon>
                        <span class="font-weight-bold">Seller Information</span>
                      </div>
                      <div class="party-details">
                        <div class="detail-item mb-2">
                          <div class="detail-label">Name</div>
                          <div class="detail-value">
                            {{ orderInfo.seller ? 
                               `${orderInfo.seller.first_name} ${orderInfo.seller.last_name || ''}`.trim() : 
                               'N/A' 
                            }}
                          </div>
                        </div>
                        <div class="detail-item mb-2" v-if="orderInfo.seller && orderInfo.seller.email">
                          <div class="detail-label">Email</div>
                          <div class="detail-value">{{ orderInfo.seller.email }}</div>
                        </div>
                        <div class="detail-item" v-if="orderInfo.seller && orderInfo.seller.phone">
                          <div class="detail-label">Phone</div>
                          <div class="detail-value">{{ orderInfo.seller.phone }}</div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="party-card">
                      <div class="party-header">
                        <v-icon color="info" class="mr-2">mdi-account-heart</v-icon>
                        <span class="font-weight-bold">Patient Information</span>
                      </div>
                      <div class="party-details">
                        <div class="detail-item mb-2">
                          <div class="detail-label">Name</div>
                          <div class="detail-value">
                            {{ orderInfo.patient ? 
                               `${orderInfo.patient.first_name} ${orderInfo.patient.last_name || ''}`.trim() : 
                               'N/A' 
                            }}
                          </div>
                        </div>
                        <div class="detail-item mb-2" v-if="orderInfo.patient && orderInfo.patient.email">
                          <div class="detail-label">Email</div>
                          <div class="detail-value">{{ orderInfo.patient.email }}</div>
                        </div>
                        <div class="detail-item" v-if="orderInfo.patient && orderInfo.patient.phone">
                          <div class="detail-label">Phone</div>
                          <div class="detail-value">{{ orderInfo.patient.phone }}</div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-tab-item>
            
            <v-tab-item>
              <v-card-text class="py-4">
                <v-timeline dense>
                  <v-timeline-item
                    color="success"
                    icon="mdi-plus-circle"
                    fill-dot
                  >
                    <template v-slot:opposite>
                      <span class="text-caption">{{ formatDate(orderInfo.created_at) }}</span>
                    </template>
                    <div class="timeline-content">
                      <h4 class="font-weight-bold">Order Created</h4>
                      <p class="text-caption grey--text ma-0">Order was successfully placed in the system</p>
                    </div>
                  </v-timeline-item>
                  
                  <v-timeline-item
                    v-if="orderInfo.order_status"
                    color="info"
                    icon="mdi-progress-check"
                    fill-dot
                  >
                    <template v-slot:opposite>
                      <span class="text-caption">{{ formatDate(orderInfo.updated_at) }}</span>
                    </template>
                    <div class="timeline-content">
                      <h4 class="font-weight-bold">Order Processed</h4>
                      <p class="text-caption grey--text ma-0">Order processing has been completed</p>
                    </div>
                  </v-timeline-item>
                  
                  <v-timeline-item
                    v-if="orderInfo.delivered_at"
                    color="success"
                    icon="mdi-check-circle"
                    fill-dot
                  >
                    <template v-slot:opposite>
                      <span class="text-caption">{{ formatDate(orderInfo.delivered_at) }}</span>
                    </template>
                    <div class="timeline-content">
                      <h4 class="font-weight-bold">Order Delivered</h4>
                      <p class="text-caption grey--text ma-0">Order has been successfully delivered</p>
                    </div>
                  </v-timeline-item>
                  
                  <v-timeline-item
                    v-if="orderInfo.cancelled_at"
                    color="error"
                    icon="mdi-close-circle"
                    fill-dot
                  >
                    <template v-slot:opposite>
                      <span class="text-caption">{{ formatDate(orderInfo.cancelled_at) }}</span>
                    </template>
                    <div class="timeline-content">
                      <h4 class="font-weight-bold">Order Cancelled</h4>
                      <p class="text-caption grey--text ma-0">Order was cancelled and will not be processed</p>
                    </div>
                  </v-timeline-item>
                </v-timeline>
              </v-card-text>
            </v-tab-item>
          </v-tabs>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-4 py-3">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            outlined
            @click="showOrderDetailsDialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel Item Confirmation Dialog -->
    <v-dialog v-model="showCancelItemDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="error white--text py-3">
          <v-icon left color="white">mdi-cancel</v-icon>
          Cancel Item
        </v-card-title>
        <v-card-text class="pt-4" v-if="cancelItemTarget">
          <v-alert type="warning" outlined dense class="mb-3">
            <span v-if="isLastItem">
              This is the only item — cancelling it will <strong>cancel the entire order</strong> and process a full refund of
              <strong>₹{{ (parseFloat(orderInfo ? orderInfo.total_amount : 0) + parseFloat(orderInfo && orderInfo.delivery_charges ? orderInfo.delivery_charges : 0)).toFixed(2) }}</strong>
              <template v-if="orderInfo && parseFloat(orderInfo.delivery_charges) > 0">
                (₹{{ parseFloat(orderInfo.total_amount || 0).toFixed(2) }} item + ₹{{ parseFloat(orderInfo.delivery_charges).toFixed(2) }} delivery)
              </template>.
            </span>
            <span v-else>
              This will cancel <strong>{{ getItemName(cancelItemTarget) }}</strong> (Qty: {{ cancelItemTarget.quantity }}) and process a partial refund of
              <strong>₹{{ parseFloat(cancelItemTarget.final_amount || 0).toFixed(2) }}</strong>.
            </span>
          </v-alert>
          <v-textarea
            v-model="cancelItemReason"
            label="Reason (optional)"
            placeholder="Why is this item being cancelled?"
            outlined
            dense
            rows="2"
            hide-details
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="pb-3 px-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showCancelItemDialog = false" :disabled="isCancellingItem">Close</v-btn>
          <v-btn color="error" dark @click="confirmCancelItem" :loading="isCancellingItem">
            <v-icon left small>mdi-cancel</v-icon>
            Cancel Item
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "OrderItemsViewPage",
  computed: {
    isSeller() {
      return this.$store.getters['auth/isSeller'];
    },
    headers() {
      const all = [
        { text: "Sr",           value: "sr_no",        sortable: false, width: "40px",                            align: "center" },
        { text: "Product Name", value: "product_info", sortable: false, width: this.isSeller ? "280px" : "140px", align: "start"  },
        { text: "Qty",          value: "quantity",     sortable: false, width: "45px",                            align: "center" },
        { text: "MRP",          value: "product_price",sortable: false, width: "70px",                            align: "end"    },
        { text: "Discount",     value: "discount",     sortable: false, width: "70px",                            align: "end"    },
        { text: "Taxable Amt",  value: "taxable",      sortable: false, width: "80px",  align: "end",   sellerHide: true },
        { text: "HSN",          value: "hsn",          sortable: false, width: "75px",  align: "center",sellerHide: true },
        { text: "GST %",        value: "gst_pct",      sortable: false, width: "55px",  align: "center",sellerHide: true },
        { text: "GST Amt",      value: "gst_amt",      sortable: false, width: "70px",  align: "end",   sellerHide: true },
        { text: "Total",        value: "total_price",  sortable: false, width: "70px",                            align: "end"    },
        { text: "Action",       value: "cancel_action",sortable: false, width: "85px",                            align: "center" },
      ];
      return this.isSeller ? all.filter(h => !h.sellerHide) : all;
    },
    canCancelItems() {
      if (!this.orderInfo) return false;
      const status = parseInt(this.orderInfo.order_status);
      return status < 3; // allow cancel if not yet shipped
    },
    isLastItem() {
      return this.orderItems.filter(i => !i.is_cancelled).length <= 1;
    },
  },
  data() {
    return {
      orderItems: [],
      orderInfo: null,
      isLoading: false,
      isExcelLoading: false,
      showOrderDetailsDialog: false,
      showCancelItemDialog: false,
      cancelItemTarget: null,
      cancelItemReason: "",
      isCancellingItem: false,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let encryptedOrderId = this.$route.params.orderId;
      
      try {
        // Get order information
        const orderResponse = await axios.get(`/orders/${encryptedOrderId}`);
        this.orderInfo = orderResponse.data.data;
        
        // Get order items
        const itemsResponse = await axios.get(`/orders/${encryptedOrderId}/items`);
        this.orderItems = itemsResponse.data.data;
        
      } catch (error) {
        console.error("Error fetching order items:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        } else {
          this.$router.push("/orders");
        }
      }
      this.isLoading = false;
    },
    
    async generateExcel() {
      this.isExcelLoading = true;
      let encryptedOrderId = this.$route.params.orderId;
      
      try {
        const response = await axios.get(`/orders/${encryptedOrderId}/items/excel`, {
          responseType: "blob",
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get filename from response headers or use default
        const contentDisposition = response.headers["content-disposition"];
        let filename = `order_items_${this.orderInfo?.order_number || 'export'}.xlsx`;
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Excel export failed:", error);
      }

      this.isExcelLoading = false;
    },
    
    getItemName(item) {
      return item.seller_product_variant?.product_variant?.name
        || item.product?.name
        || 'Item';
    },
    promptCancelItem(item) {
      this.cancelItemTarget  = item;
      this.cancelItemReason  = "";
      this.showCancelItemDialog = true;
    },
    async confirmCancelItem() {
      if (!this.cancelItemTarget || !this.orderInfo) return;
      this.isCancellingItem = true;
      try {
        const res = await axios.post(
          `/orders/${this.orderInfo.id}/items/${this.cancelItemTarget.id}/cancel`,
          { cancellation_reason: this.cancelItemReason || null }
        );
        if (res.data.success) {
          this.showCancelItemDialog = false;
          await this.getData();
        }
      } catch (e) {
        console.error("Cancel failed:", e);
      } finally {
        this.isCancellingItem = false;
      }
    },

    getOrderStatusColor(order) {
      if (order.cancelled_at) {
        return "error";
      }
      if (order.delivered_at) {
        return "success";
      }
      if (order.order_status) {
        return "info";
      }
      return "warning";
    },
    
    getOrderStatusText(order) {
      if (order.cancelled_at) {
        return "Cancelled";
      }
      if (order.delivered_at) {
        return "Delivered";
      }
      if (order.order_status) {
        return "Processed";
      }
      return "Pending";
    },
    
    getOrderStatusIcon(order) {
      if (order.cancelled_at) {
        return "mdi-close-circle";
      }
      if (order.delivered_at) {
        return "mdi-check-circle";
      }
      if (order.order_status) {
        return "mdi-progress-check";
      }
      return "mdi-clock-outline";
    },
    
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    
    formatDateShort(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
    
    getTimeAgo(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return "1 day ago";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
      return `${Math.ceil(diffDays / 30)} months ago`;
    },
    
    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },
    
    getQuantityColor(quantity) {
      if (quantity >= 20) return "success";
      if (quantity >= 10) return "warning";
      if (quantity >= 5) return "orange";
      return "info";
    },
    
    getQuantityIcon(quantity) {
      if (quantity >= 20) return "mdi-check-circle";
      if (quantity >= 10) return "mdi-alert-circle";
      if (quantity >= 5) return "mdi-minus-circle";
      return "mdi-information";
    },
    
    getMrp(item) {
      return parseFloat(
        item.seller_product_variant && item.seller_product_variant.maximum_retail_price
          ? item.seller_product_variant.maximum_retail_price
          : (item.amount || 0)
      );
    },

    getItemDiscount(item) {
      const mrpTotal = this.getMrp(item) * (item.quantity || 1);
      const finalTotal = parseFloat(item.final_amount || 0);
      return Math.max(0, mrpTotal - finalTotal);
    },

    getTotalQty() {
      return this.orderItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    },

    calculateGrandTotal() {
      return this.orderItems.reduce((total, item) => {
        return total + this.getMrp(item) * (item.quantity || 1);
      }, 0).toFixed(2);
    },

    getTotalDiscount() {
      return this.orderItems.reduce((sum, item) => sum + this.getItemDiscount(item), 0);
    },

    getSubtotal() {
      return this.orderItems.reduce((sum, item) => sum + parseFloat(item.final_amount || 0), 0);
    },

    getTotalGst() {
      return this.orderItems.reduce((sum, item) => sum + parseFloat(item.tax_amount || 0), 0);
    },

    getGstPct() {
      const taxable = this.orderItems.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
      const gst = this.getTotalGst();
      return taxable > 0 ? Math.round((gst / taxable) * 100) : 0;
    },

    getPayableAmount() {
      return this.getSubtotal() + parseFloat(this.orderInfo && this.orderInfo.delivery_charges ? this.orderInfo.delivery_charges : 0);
    },

    getAverageItemValue() {
      if (this.orderItems.length === 0) return "0.00";
      const total = this.orderItems.reduce((sum, item) => {
        return sum + this.getMrp(item) * (item.quantity || 1);
      }, 0);
      return (total / this.orderItems.length).toFixed(2);
    },
    
    showOrderDetails() {
      this.showOrderDetailsDialog = true;
    },
  },
};
</script>

<style scoped>
/* Page Header */
.page-header-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-left: 4px solid #1976d2;
}

/* Order Summary Enhanced */
.order-summary-enhanced {
  background: #ffffff;
}

.order-summary-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px 4px 0 0;
}

.summary-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.summary-icon {
  width: 32px !important;
  height: 32px !important;
  margin-right: 12px;
}

.summary-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin-bottom: 2px;
}

.summary-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

/* Enhanced Table Styles */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 0 0 4px 4px;
}

.enhanced-table >>> th {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600 !important;
  border-bottom: 2px solid #dee2e6 !important;
}

.enhanced-table >>> td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f0f0f0 !important;
  vertical-align: top !important;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

/* Product Info */
.product-info-container {
  max-width: 300px;
}

.product-avatar {
  border: 2px solid #e0e0e0;
  border-radius: 8px !important;
}

.product-details {
  min-width: 0;
  flex: 1;
}

/* Quantity Styling */
.quantity-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 4px;
}

.quantity-chip {
  font-weight: bold;
  min-width: 50px;
}

/* Price Styling */
.price-container {
  text-align: left;
  padding-top: 4px;
}

/* Date Styling */
.date-container {
  text-align: left;
  padding-top: 4px;
}

/* Status Styling */
.status-chip {
  font-weight: 500;
}

/* Summary Footer */
.summary-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

/* Order Details Dialog */
.order-details-dialog {
  max-height: 90vh;
  overflow: hidden;
}

.order-details-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px 4px 0 0;
}

.order-details-tabs >>> .v-tabs-bar {
  background-color: #f8f9fa;
}

.detail-item {
  margin-bottom: 12px;
}

.detail-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

/* Party Cards */
.party-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
  background: #fafafa;
}

.party-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.party-details {
  padding-top: 8px;
}

/* Timeline Content */
.timeline-content h4 {
  font-size: 16px;
  margin-bottom: 4px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .order-details-dialog {
    margin: 8px;
    max-width: calc(100vw - 16px) !important;
  }
  
  .summary-item {
    justify-content: center;
    text-align: center;
    margin-bottom: 16px;
  }
  
  .party-card {
    margin-bottom: 16px;
  }
  
  .enhanced-table >>> td {
    padding: 12px 4px !important;
    font-size: 13px;
  }
  
  .product-info-container {
    max-width: none;
  }
  
  .product-avatar {
    width: 32px !important;
    height: 32px !important;
  }
}

/* Animation Effects */
.v-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.v-btn {
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.enhanced-table >>> tbody tr {
  transition: background-color 0.2s ease;
}

/* Legacy Styles (for backward compatibility) */
.striped-table tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.order-summary-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.order-summary-card .v-card__text {
  color: white;
}

/* ── Enhanced table ─────────────────────── */
.enhanced-table >>> .v-data-table__wrapper thead tr th {
  background: #f5f7fa !important;
  color: #546e7a !important;
  font-size: 0.68rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.06em !important;
  border-bottom: 2px solid #e0e0e0 !important;
  padding: 10px 12px !important;
  white-space: nowrap;
}
.enhanced-table >>> .v-data-table__wrapper tbody tr {
  transition: background 0.1s;
}
.enhanced-table >>> .v-data-table__wrapper tbody tr:hover {
  background: #f0f7ff !important;
}
.enhanced-table >>> .v-data-table__wrapper tbody td {
  font-size: 0.82rem !important;
  color: #2d3748;
  border-bottom: 1px solid #f0f0f0 !important;
  padding: 10px 12px !important;
}

/* ── Invoice Summary ───────────────────── */
/* Summary Card */
.summary-card { border-radius: 8px; overflow: hidden; }
.summary-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
}
.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  font-size: 0.82rem;
}
.summary-row--payable {
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-top: 2px solid #81c784;
  padding: 14px 16px;
}
.summary-label { color: #546e7a; }
.summary-value { font-weight: 500; color: #2d3748; }
.summary-value--payable { font-weight: 800; font-size: 1rem; color: #2e7d32; }

/* Table */
.items-table >>> thead tr th {
  background: #f5f7fa !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #455a64 !important;
}
.items-table >>> tbody tr:hover {
  background: #f8f9fe !important;
}
</style>